import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ExternalLinkIcon } from "lucide-react";
import { useState } from "react";

import { useAnalysisUsage } from "@/features/analysis";
import { DeleteAccountDialog, useLogout } from "@/features/auth";
import { useApiErrorMessage } from "@/shared/api";
import { cn } from "@/shared/lib";
import { BlockError } from "@/shared/ui/block-error";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";

import { ProfileSection } from "./ProfileSection";

// Больше разборов в сутки — без шкалы, только числом.
const METER_MAX = 10;

const PER_DAY = msg({ message: "{count} per 24 hours" });
const USED = msg({
  message: "{count, plural, one {# in the last 24 hours} other {# in the last 24 hours}}",
});
const LEFT = msg({
  message:
    "{remaining, plural, one {# analysis left of {limit}} other {# analyses left of {limit}}}",
});

type AccountSectionProps = {
  // Steam64 id: ссылка на профиль Steam; у служебной учётки — `null`.
  steamId: string | null;
};

// «Аккаунт»: вход через Steam со ссылкой на профиль, лимит разборов за сутки (сколько
// осталось и когда освободится следующий; без лимита — сколько разобрано за сутки), выход
// и удаление аккаунта.
export const AccountSection = ({ steamId }: AccountSectionProps) => {
  const { i18n, t } = useLingui();
  const usage = useAnalysisUsage();
  const logout = useLogout();
  const logoutError = useApiErrorMessage(logout.error);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const quota = () => {
    if (usage.isError) {
      return (
        <BlockError
          title={t`Could not load the analysis limit`}
          retrying={usage.isFetching}
          onRetry={() => void usage.refetch()}
        />
      );
    }
    if (usage.data === undefined) {
      return (
        <div className="flex items-center justify-between gap-4">
          <span className="flex flex-col gap-1.5">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-48" />
          </span>
          <Skeleton className="h-5 w-20" />
        </div>
      );
    }
    const { limit, used, resets_at: resetsAt } = usage.data;
    if (limit === null) {
      return (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <span className="flex flex-col">
            <span>
              <Trans>Analyses for the day</Trans>
            </span>
            <span className="text-[13px] text-subtle">
              {i18n._({ ...USED, values: { count: used } })}
            </span>
          </span>
          <span className="font-semibold text-good">
            <Trans>No limit</Trans>
          </span>
        </div>
      );
    }
    const remaining = Math.max(limit - used, 0);
    const next =
      resetsAt === null
        ? null
        : new Intl.DateTimeFormat(i18n.locale, { hour: "2-digit", minute: "2-digit" }).format(
            new Date(resetsAt),
          );
    return (
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <span className="flex flex-col">
          <span>
            <Trans>Analyses for the day</Trans>
          </span>
          <span className="text-[13px] text-subtle">
            {i18n._({ ...PER_DAY, values: { count: limit } })}
            {next !== null && <> · {t`next one frees up at ${next}`}</>}
          </span>
        </span>
        <span className="inline-flex items-center gap-2.5">
          {limit <= METER_MAX && (
            <span aria-hidden="true" className="inline-flex gap-1">
              {Array.from({ length: limit }, (_, index) => (
                <span
                  key={index}
                  className={cn(
                    "h-[22px] w-3.5 rounded-[3px] border",
                    index < remaining
                      ? "border-accent bg-accent"
                      : "border-border bg-surface-raised",
                  )}
                />
              ))}
            </span>
          )}
          <span className="font-mono font-semibold">
            {i18n._({ ...LEFT, values: { remaining, limit } })}
          </span>
        </span>
      </div>
    );
  };

  return (
    <ProfileSection title={<Trans>Account</Trans>} className="gap-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <span className="flex flex-col">
          <span>
            <Trans>Signed in with Steam</Trans>
          </span>
          <span className="text-[13px] text-subtle">
            <Trans>Your name and avatar update every time you sign in</Trans>
          </span>
        </span>
        {steamId !== null && (
          <a
            href={`https://steamcommunity.com/profiles/${steamId}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-10 items-center gap-1.5 text-sm font-semibold text-accent no-underline hover:text-accent-strong"
          >
            <Trans>Steam profile</Trans>
            <ExternalLinkIcon aria-hidden="true" className="size-4" />
          </a>
        )}
      </div>
      {quota()}
      <div className="mt-auto flex flex-wrap gap-2.5 border-t border-border pt-4">
        <Button
          variant="outline"
          className="h-11"
          loading={logout.isPending}
          onClick={() => {
            logout.mutate({});
          }}
        >
          <Trans>Sign out</Trans>
        </Button>
        <Button
          variant="outline"
          className="h-11 border-bad/40 text-bad hover:bg-bad/10"
          onClick={() => {
            setDeleteOpen(true);
          }}
        >
          <Trans>Delete account</Trans>
        </Button>
      </div>
      {logoutError !== null && (
        <p role="alert" className="text-sm text-bad">
          {logoutError}
        </p>
      )}
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />
    </ProfileSection>
  );
};
